/**
 * Number display for the terminal: one rule set everywhere. Prices keep the instrument's tick decimals with digit
 * grouping, quantities drop trailing zeros, money is always two decimals with a sign where it is P&L, book sizes keep
 * four significant digits so a column of them reads at a glance.
 */

import { numberLocaleFor, parseLang } from "@prop/i18n";

/**
 * The reader's language as the app set it on <html lang>, so the terminal's numbers match the rest of the page. The
 * build-time prerender has no document: it names the language of the page it renders (ADR-0062), the same tag the
 * page's <html lang> carries, so a prerendered page and its hydration format numbers alike.
 */
let serverLocale: string | undefined;
export const setServerLocale = (tag: string): void => {
  serverLocale = tag;
};
const loc = (): string | undefined => {
  const tag = typeof document === "undefined" ? serverLocale : document.documentElement.lang;
  const lang = parseLang(tag);
  // Digits through numberLocaleFor: Spanish takes the platform's comma shape (1 199,50; ADR-0065), not CLDR es-ES.
  return lang ? numberLocaleFor(lang) : tag || undefined;
};
const grouped = (v: number, decimals: number) => v.toLocaleString(loc(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/** Decimal places of a tick or step string ("0.01" → 2, "1" → 0). */
export const decimalsOf = (step: string): number => (step.includes(".") ? (step.split(".")[1] ?? "").replace(/0+$/, "").length || 0 : 0);

/** A price at the instrument's tick: 80 147, 1.08421. */
export function fmtPx(v: string | number | null | undefined, tickSize: string): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? grouped(n, decimalsOf(tickSize)) : "—";
}

/** A quantity without trailing zeros: "0.00100000" → 0.001, 2.5 → 2.5. */
export function fmtQty(v: string | number | null | undefined, maxDecimals = 8): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = Math.abs(Number(v));
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(loc(), { maximumFractionDigits: maxDecimals });
}

/** Money: $1,234.56; with `signed`, +$12.30 / −$4.10. */
export function fmtUsd(v: string | number | null | undefined, signed = false): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  // Whole dollars from $1,000 up (owner, 2026-10-30: "no need to show cents everywhere"); cents below, where they matter.
  const body = `$${grouped(Math.abs(n), Math.abs(n) >= 1000 ? 0 : 2)}`;
  if (n < 0) return `−${body}`;
  return signed && n > 0 ? `+${body}` : body;
}

/** Book and tape sizes: four significant digits, no trailing zeros (38.42, 5.439, 0.07173). */
export function fmtSize(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1000) return String(Math.round(v));
  return String(Number(v.toPrecision(4)));
}

/**
 * A quantity for a share of the maximum, rounded down to a "nice" increment (two significant digits of the maximum)
 * so the slider gives 0.31 BTC rather than 0.31192 BTC; never below the instrument step.
 */
export function niceQty(max: number, pct: number, step: number, minQty: number): number {
  const raw = (max * pct) / 100;
  if (raw <= 0) return minQty;
  const inc = Math.max(step, 10 ** (Math.floor(Math.log10(Math.max(max, step))) - 1));
  return Math.max(minQty, Math.floor(raw / inc) * inc);
}
